import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Crown, Mail } from 'lucide-react';
import { supabase } from '../supabaseClient';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { showErrorToast } from '@/lib/toast';

// Story 073 — Forgot password entry. Submits an email; on success we
// flip to a persistent "check your email" screen. Don't differentiate
// success/failure based on account existence — Supabase's
// resetPasswordForEmail returns success either way to avoid account
// enumeration, and our UI mirrors that.

export function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      // The auth-email-hook (070c) wraps this in our /auth/confirm
      // page with token_hash, so we just pass the final destination —
      // passing /auth/confirm here would double-wrap and AuthConfirmPage
      // would re-trigger with no token after the first verifyOtp.
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });
      if (error) {
        showErrorToast(t('forgot_password.errors.unexpected'));
        return;
      }
      setSubmitted(true);
    } catch {
      showErrorToast(t('forgot_password.errors.unexpected'));
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <Card
          data-testid="check-your-email-screen"
          className="w-full max-w-md"
        >
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-accent/20">
              <Mail className="h-6 w-6 text-accent-foreground" />
            </div>
            <CardTitle>{t('forgot_password.check_email_title')}</CardTitle>
            <CardDescription>
              {t('forgot_password.check_email_body')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">{t('forgot_password.back_to_login')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      data-testid="forgot-password-page"
      className="flex min-h-screen items-center justify-center bg-background px-4 py-12"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-accent/20">
            <Crown className="h-6 w-6 text-accent-foreground" />
          </div>
          <CardTitle className="text-2xl">{t('forgot_password.title')}</CardTitle>
          <CardDescription>{t('forgot_password.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">{t('forgot_password.email_label')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                placeholder={t('forgot_password.email_placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-password-email-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || email.length === 0}
              data-testid="forgot-password-submit-button"
              className="w-full"
            >
              {loading
                ? t('forgot_password.submitting')
                : t('forgot_password.submit')}
            </Button>
            <Button asChild variant="ghost" className="w-full">
              <Link to="/login">{t('forgot_password.back_to_login')}</Link>
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
